import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  RadioGroup,
  Grid,
  FormLabel,
  FormControl,
  FormControlLabel,
  Radio,
  Typography,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { grey } from "@mui/material/colors";
import { defaultStyle } from "components/Modals";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { saveProductionParkLimit } from "api/resources/productionPark";

interface ServiceModalProps {
  productionParkId: number;
  productionParkType: string;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  dispatch: any;
  setSnackbar: any;
}

const ServiceModal = ({
  productionParkId,
  productionParkType,
  setOpenModal,
  dispatch,
  setSnackbar,
}: ServiceModalProps) => {
  const forecastTypes: Record<string, string> = {
    SOLAR: "SUN",
    WIND: "WIND",
  };

  const forecastType = forecastTypes[productionParkType] ?? "";

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    forecastType: forecastType,
    fromDateTime: null,
    toDateTime: null,
    type: "MAX_OUTPUT",
    value: 0,
  });

  const handleSubmit = () => {
    setLoading(true);
    const postData = {
      productionParkId: productionParkId,
      forecastType: formData.forecastType,
      type: formData.type,
      fromDateTime: dayjs(formData.fromDateTime).utc().format(),
      toDateTime: dayjs(formData.toDateTime).utc().format(),
      value:
        formData.type === "OUTPUT_AVAILABILITY"
          ? formData.value / 100
          : formData.value,
    };

    saveProductionParkLimit(postData)
      .then(() => {
        dispatch();
        setLoading(false);
        setOpenModal(false);
        setSnackbar({
          type: "success",
          message: "Limitation set successfully",
        });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        setSnackbar({
          type: "error",
          message: error.response.data.message,
        });
      });
  };

  return (
    <Box sx={defaultStyle}>
      <Typography id="modal-modal-title" variant="h5" component="h5">
        Add limitation
      </Typography>
      <Typography
        id="modal-modal-title"
        component="p"
        sx={{ color: grey[700], mt: "0.6rem", mb: "1rem" }}
      >
        Set a service period for the component. This will remove the production
        capacity from the forecast generation.
      </Typography>
      {productionParkType === "HYBRID" && (
        <FormControl sx={{ mt: 1 }}>
          <FormLabel>Affected components</FormLabel>
          <RadioGroup
            row
            name="row-radio-buttons-limiting-park-type"
            value={formData.forecastType}
            onChange={(e) => {
              const formDataCopy = { ...formData };

              formDataCopy.forecastType = (e.target as HTMLInputElement).value;

              setFormData(formDataCopy);
            }}
          >
            <FormControlLabel value="SUN" control={<Radio />} label="Solar" />
            <FormControlLabel value="WIND" control={<Radio />} label="Wind" />
          </RadioGroup>
        </FormControl>
      )}
      <Grid container spacing={0} sx={{ mb: "2rem" }}>
        <Grid item sm={8}>
          <Grid container sx={{ mt: "0.5rem", mb: "1rem" }}>
            <Grid item md={6}>
              <DateTimePicker
                label={"From"}
                slotProps={{
                  textField: { size: "small" },
                  clearButton: { size: "small" },
                }}
                minutesStep={15}
                skipDisabled
                value={formData.fromDateTime}
                onChange={(value) => {
                  const formDataCopy = { ...formData };

                  formDataCopy.fromDateTime = value;

                  setFormData(formDataCopy);
                }}
                sx={{ mr: "0.8rem" }}
              />
            </Grid>
            <Grid item md={6}>
              <DateTimePicker
                label={"To"}
                sx={{ background: "white" }}
                slotProps={{ textField: { size: "small" } }}
                minDate={dayjs(formData.fromDateTime)}
                minutesStep={15}
                skipDisabled
                value={formData.toDateTime}
                onChange={(value) => {
                  const formDataCopy = { ...formData };

                  formDataCopy.toDateTime = value;

                  setFormData(formDataCopy);
                }}
              />
            </Grid>
          </Grid>
          <Box>
            <FormControl>
              <FormLabel>Limiting type</FormLabel>
              <RadioGroup
                row
                name="row-radio-buttons-group"
                value={formData.type}
                onChange={(e) => {
                  const formDataCopy = { ...formData };

                  formDataCopy.type = (e.target as HTMLInputElement).value;

                  setFormData(formDataCopy);
                }}
              >
                <FormControlLabel
                  value="MAX_OUTPUT"
                  control={<Radio />}
                  label="Max output"
                />
                <FormControlLabel
                  value="OUTPUT_AVAILABILITY"
                  control={<Radio />}
                  label="Output availability"
                />
              </RadioGroup>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <TextField
                id="value"
                name="value"
                label="Value"
                type="number"
                size="small"
                InputProps={{
                  inputProps: {
                    min: 0,
                    max: formData.type === "MAX_OUTPUT" ? null : 100,
                  },
                  endAdornment: formData.type === "MAX_OUTPUT" ? "kW" : "%",
                }}
                sx={{ maxWidth: "125px" }}
                value={formData.value}
                onChange={(e) => {
                  const formDataCopy = { ...formData };
                  let value = e.target.value;
                  const max = formData.type === "MAX_OUTPUT" ? null : 100;

                  if (value === "") {
                    value = "";
                  } else if (max && Number(value) >= max) {
                    value = max;
                  } else if (Number(value) < 0) {
                    value = 0;
                  } else {
                    value = Number(value);
                  }

                  formDataCopy.value = value;

                  setFormData(formDataCopy);
                }}
              />
            </FormControl>
          </Box>
        </Grid>
        <Grid item sm={12} sx={{ mt: "2rem" }}>
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingIndicator="Saving..."
            onClick={() => handleSubmit()}
            disabled={loading || !formData.fromDateTime || !formData.toDateTime}
          >
            Save limitation
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ServiceModal;
